import { useState, useEffect } from "react"
import QRCode from "qrcode"
import { useTranslation } from "react-i18next"

interface PaymentQRProps {
    /** IBAN of the recipient account */
    bankAccount: string
    variableSymbol: string | null
    /** Amount in minor units (cents, e.g. 50000 = 500 CZK) */
    amountCents: number
    currency: string
    arealName: string
}

/**
 * Builds a Short Payment Descriptor (SPD) string per the Czech QR Platba standard.
 *
 * Format: SPD*1.0*ACC:{IBAN}*AM:{amount}*CC:{currency}*MSG:Rezervace[*X-VS:{vs}]
 *
 * The ACC field MUST contain an IBAN — native Czech "account/bankCode" format is
 * not accepted by banking apps.
 */
function buildSpdString(
    iban: string,
    variableSymbol: string | null,
    amountCents: number,
    currency: string,
): string {
    const amount = (amountCents / 100).toFixed(2)
    const parts = [
        "SPD*1.0",
        `ACC:${iban}`,
        `AM:${amount}`,
        `CC:${currency}`,
        "MSG:Rezervace",
    ]
    if (variableSymbol) {
        parts.push(`X-VS:${variableSymbol}`)
    }
    return parts.join("*")
}

export const PaymentQR = ({
    bankAccount,
    variableSymbol,
    amountCents,
    currency,
    arealName,
}: PaymentQRProps) => {
    const { t } = useTranslation()
    const [dataUrl, setDataUrl] = useState<string | null>(null)
    const [generating, setGenerating] = useState(true)
    const [genError, setGenError] = useState(false)

    useEffect(() => {
        let cancelled = false
        setGenerating(true)
        setGenError(false)

        const spd = buildSpdString(bankAccount, variableSymbol, amountCents, currency)

        QRCode.toDataURL(spd, {
            errorCorrectionLevel: "M",
            margin: 2,
            width: 256,
            color: {
                dark: "#000000",
                light: "#ffffff",
            },
        })
            .then((url) => {
                if (!cancelled) {
                    setDataUrl(url)
                    setGenerating(false)
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setGenError(true)
                    setGenerating(false)
                }
            })

        return () => {
            cancelled = true
        }
    }, [bankAccount, variableSymbol, amountCents, currency])

    return (
        <div className="flex flex-col items-center gap-3 bg-white rounded-2xl border border-slate-200 p-5 shadow-md">
            <p className="text-xs font-black uppercase tracking-widest text-slate-500">
                {t("components.paymentQr.title", { arealName })}
            </p>

            <div className="w-[200px] h-[200px] flex items-center justify-center rounded-xl overflow-hidden border border-slate-100 bg-slate-50">
                {generating && (
                    <span className="text-xs font-bold text-slate-400">{t("components.paymentQr.generating")}</span>
                )}
                {genError && (
                    <span className="text-xs font-bold text-red-400">{t("components.paymentQr.error")}</span>
                )}
                {!generating && !genError && dataUrl !== null && (
                    <img
                        src={dataUrl}
                        alt={t("components.paymentQr.imageAlt")}
                        width={200}
                        height={200}
                        className="block"
                    />
                )}
            </div>

            <p className="text-xs font-bold text-slate-500 text-center">
                {t("components.paymentQr.scanToPay")}
            </p>
        </div>
    )
}
